import React from "react";
import styled from "styled-components";
import Button from "../Button/Button";

/**
 * ButtonGroup Component following Apple Human Interface Guidelines
 *
 * Button Order (Left to Right):
 * 1. Destructive actions (Delete, Remove, Cancel destructive operations)
 * 2. Secondary actions (Cancel, Back, Neutral actions)
 * 3. Primary actions (Submit, Continue, Save, Done, Confirm)
 *
 * Usage:
 * <ButtonGroup>
 *   <Button variant="error">Delete</Button>     // Destructive (leftmost)
 *   <Button variant="secondary">Cancel</Button> // Secondary
 *   <Button variant="primary">Save</Button>     // Primary (rightmost)
 * </ButtonGroup>
 */

const ButtonGroup = ({
  children,
  alignment = "right", // 'left', 'center', 'right', 'space-between'
  spacing = "normal", // 'tight', 'normal', 'loose'
  direction = "row", // 'row', 'column'
  className,
  ...props
}) => {
  return (
    <ButtonGroupContainer
      alignment={alignment}
      spacing={spacing}
      direction={direction}
      className={className}
      {...props}
    >
      {children}
    </ButtonGroupContainer>
  );
};

const ButtonGroupContainer = styled.div`
  display: flex;
  flex-direction: ${({ direction }) => direction};
  align-items: center;

  justify-content: ${({ alignment }) => {
    switch (alignment) {
      case "left":
        return "flex-start";
      case "center":
        return "center";
      case "right":
        return "flex-end";
      case "space-between":
        return "space-between";
      case "space-around":
        return "space-around";
      default:
        return "flex-end";
    }
  }};

  gap: ${({ spacing, direction }) => {
    const isColumn = direction === "column";
    switch (spacing) {
      case "tight":
        return isColumn ? "8px" : "8px";
      case "normal":
        return isColumn ? "12px" : "12px";
      case "loose":
        return isColumn ? "16px" : "16px";
      default:
        return isColumn ? "12px" : "12px";
    }
  }};

  width: 100%;
  flex-wrap: wrap;

  /* Ensure proper button order is maintained on mobile */
  @media screen and (max-width: 768px) {
    flex-direction: ${({ direction }) =>
      direction === "row" ? "column-reverse" : "column"};
    gap: 12px;

    /* On mobile, stack vertically with primary action on top */
    & > button:last-child {
      order: -1;
    }
  }

  /* Small screens - adjust spacing */
  @media screen and (max-width: 480px) {
    gap: 8px;
  }
`;

// Pre-configured common button group patterns
export const FormActions = ({
  onCancel,
  onSubmit,
  onDelete,
  cancelText = "Cancel",
  submitText = "Save",
  deleteText = "Delete",
  isSubmitting = false,
  showDelete = false,
  showCancel = true,
  ...props
}) => {
  return (
    <ButtonGroup alignment="right" {...props}>
      {showDelete && onDelete && (
        <Button
          variant="error"
          size="medium"
          onClick={onDelete}
          disabled={isSubmitting}
        >
          {deleteText}
        </Button>
      )}
      {showCancel && onCancel && (
        <Button
          variant="secondary"
          size="medium"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {cancelText}
        </Button>
      )}
      {onSubmit && (
        <Button
          variant="primary"
          size="medium"
          onClick={onSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      )}
    </ButtonGroup>
  );
};

export const ConfirmationActions = ({
  onCancel,
  onConfirm,
  cancelText = "Cancel",
  confirmText = "Confirm",
  isDestructive = false,
  isLoading = false,
  ...props
}) => {
  return (
    <ButtonGroup alignment="right" {...props}>
      <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
        {cancelText}
      </Button>
      <Button
        variant={isDestructive ? "error" : "primary"}
        onClick={onConfirm}
        loading={isLoading}
        disabled={isLoading}
      >
        {confirmText}
      </Button>
    </ButtonGroup>
  );
};

export const NavigationActions = ({
  onBack,
  onNext,
  onSkip,
  backText = "Back",
  nextText = "Continue",
  skipText = "Skip",
  canGoBack = true,
  canGoNext = true,
  showSkip = false,
  isLoading = false,
  ...props
}) => {
  return (
    <ButtonGroup alignment="space-between" {...props}>
      <div>
        {showSkip && onSkip && (
          <Button variant="subtle" onClick={onSkip} disabled={isLoading}>
            {skipText}
          </Button>
        )}
      </div>
      <div style={{ display: "flex", gap: "12px" }}>
        {onBack && (
          <Button
            variant="secondary"
            onClick={onBack}
            disabled={!canGoBack || isLoading}
          >
            {backText}
          </Button>
        )}
        {onNext && (
          <Button
            variant="primary"
            onClick={onNext}
            disabled={!canGoNext}
            loading={isLoading}
          >
            {nextText}
          </Button>
        )}
      </div>
    </ButtonGroup>
  );
};

export default ButtonGroup;
